import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Play, Trophy } from 'lucide-react'
import { HeroBanner } from '@renderer/components/ui/HeroBanner'
import { useUser } from '@renderer/context/UserContext'
import { CurrentActivity } from '@renderer/components/ui/CurrentActivity'
import { LearningProgress } from '@renderer/components/ui/LearningProgress'
import { AvailableCourses } from '@renderer/components/ui/AvailableCourses'
import { coursesAPI } from '../../api/courses'
import { dashboardAPI } from '../../api/dashboard'
import { useBreadcrumbs } from '@renderer/context/BreadcrumbContext'

const DashboardStatsCard = (_props: { count: number; streak: number }): React.ReactElement => (
  <div
    className="flex items-center justify-center"
    style={{
      width: '180px',
      height: '180px',
      background: '#F7ECDC',
      borderRadius: '32px',
      border: '1px solid #e0d4be',
      backdropFilter: 'blur(4px)',
      boxShadow: '0 4px 24px rgba(0,0,0,0.07)'
    }}
  >
    <Trophy className="w-16 h-16 text-[#7a6020]" />
  </div>
)


export const Dashboard = () => {
  const navigate = useNavigate()
  const { user } = useUser()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [streak, setStreak] = useState(0)

  useEffect(() => {
    setBreadcrumbs([{ label: 'Home' }])
  }, [])

  const getFirstName = (): string => {
    if (!user?.email) return 'User'
    const name = user.email.split('@')[0]
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  const getGreeting = (): string => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await coursesAPI.getAllCourses()
        setCourses(data)
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    fetchCourses()
  }, [])

  useEffect(() => {
    const fetchStreak = async () => {
      try {
        const data = await dashboardAPI.getDayStreak()
        setStreak(data.day_streak)
      } catch {
        // ignore
      }
    }
    fetchStreak()
  }, [])

  return (
    <div className="max-w-[1200px] mx-auto space-y-6">
      <HeroBanner
        tag="Learning Dashboard"
        title={`${getGreeting()}, ${getFirstName()}`}
        description="You're making great progress! Continue your certification journey and hit your weekly goal."
        primaryButton={{
          text: 'Continue Learning',
          onClick: () => navigate('/main'),
          icon: <Play className="w-4 h-4 fill-white" />
        }}
        secondaryButton={{
          text: 'View Path',
          onClick: () => navigate('/main')
        }}
        rightContent={<DashboardStatsCard count={loading ? 0 : courses.length} streak={streak} />}
      />

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-3/5 rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.07)]" style={{ minHeight: '420px' }}>
          <CurrentActivity />
        </div>
        <div className="w-full lg:w-2/5 bg-white rounded-2xl border border-[#e0d4be] p-6 shadow-[0_4px_24px_rgba(0,0,0,0.07)]">
          <LearningProgress />
        </div>
      </div>

      <AvailableCourses limit={3} onBrowse={() => navigate('/main')} />
    </div>
  )
}